import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DescriptionOutlined, PublishOutlined } from '@material-ui/icons'

import { Calendar, DrawerBodyWrapper, DrawerFooterWrapper, RadioButton, TOAST_TYPE } from '@base'
import { Button, FormElementContainer, Grid, Input, Label, Select } from '@baseV2'
import { enquiryStatus, isEnquiryEditableStatus, MODAL_TYPES, strings } from '@constants'
import { updateEnquiryCostMetrics, updateOrderCostMetrics, uploadPunchOrderFile } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer, toggleFileViewer, toggleModal, updateDrawerMeta } from '@data/state/action/root'
import AsteriskIcon from '@assets/icons/AsteriskIcon'
import { isSalesAdmin } from '@utils/adminFilter'

import { calculateCreditDays } from '../AddCostMetrics/AddCostMetrics'
import { booltypes, deriveFormState, discountingPartnerTypes, logisticsHandlerTypes, pricingTypes } from './deriveFormState'
import MetricsData from './MetricsData'
import PurchaseManagerDetailsModal from './PurchaseManagerDetailsModal'
import SiteManagerDetailsModal from './SiteManagerDetailsModal'
import {
	FileActions,
	FormGridWrapper,
	FooterActions,
	IconActionButton,
	RadioGroupRow,
	RadioOption,
	RadioOptionLabel,
	SectionRowCard,
	SectionRowTitle,
	UploadIconButton,
} from './styles'

// po date of an order cannot be changed by a sales admin
const isPoDateBlockedForRole = (orderId, role) => Boolean(orderId) && isSalesAdmin(role, false)

const AddCostMetricsV2 = ({
	enquiry,
	edit = false,
	order = null,
	orderDate = null,
	userId = null,
	enquiryId = null,
	update,
	pastDelays = 0,
	orderId,
	viewOnly,
}) => {
	const dispatch = useDispatch()
	const [formState, setFormState] = useState(deriveFormState())
	const [totalDays, setTotalDays] = useState(0)
	const [isPurchaseManagerModalOpen, setIsPurchaseManagerModalOpen] = useState(false)
	const [isSiteManagerModalOpen, setIsSiteManagerModalOpen] = useState(false)
	const loggedInUser = useSelector((state) => state.getIn(['user', 'user']))

	useEffect(() => {
		if (order) {
			setFormState(deriveFormState(order, viewOnly))
			setTotalDays((_s) => _s + calculateCreditDays(order?.buyer_list?.[0]?.payment_terms_list, pastDelays))
		} else if (enquiry) {
			setFormState(deriveFormState(enquiry, !isEnquiryEditableStatus(enquiry) || viewOnly))
			setTotalDays((_s) => _s + calculateCreditDays(enquiry?.payment_terms_list, pastDelays))
		}
	}, [order?.order_id, enquiry?.enquiry_id])

	const updateState = (field) => (value) => {
		setFormState((_s) => ({ ..._s, [field]: { ..._s[field], value: value, dirty: true } }))

		if (field === 'cm_per_mt' || field === 'marketing_expense') {
			if (field === 'cm_per_mt' && formState?.marketing_expense?.value !== '') {
				updateState('net_margin_per_mt')(value - formState?.marketing_expense?.value)
			} else if (field === 'marketing_expense' && formState?.cm_per_mt?.value !== '') {
				updateState('net_margin_per_mt')(formState?.cm_per_mt?.value - value)
			} else {
				updateState('net_margin_per_mt')(0)
			}
			if (value === '') {
				updateState('net_margin_per_mt')(0)
			}
		}
	}

	let [totalQuantity, totalPoAmount, hasLC, isBitumen, isNonFerrous] = useMemo(() => {
		let _totalQuantity,
			_totalPoAmount,
			_hasLC = false,
			_isBitumen = false,
			_isNonFerrous = false
		if (order?.order_id) {
			_totalQuantity = order?.buyer_list?.[0]?.order_items?.reduce((acc, item) => acc + item?.ordered_weight, 0) / 1000
			_totalPoAmount = order?.buyer_list?.[0]?.order_items?.reduce((acc, item) => acc + item?.price_per_kg * item?.ordered_weight, 0)
			_hasLC = order?.buyer_list?.[0]?.payment_terms_list?.find((item) => item.payment_term === 'LC')
			_isBitumen = order?.commodity?.commodity_id === 'CON9VcB-5122' //bitumen's commodity id
			_isNonFerrous = ['COtx2Sx-1131', 'COd9sRH-8511', 'COLRzke-3499', 'COLRzke-3791'].includes(order?.commodity?.commodity_id) //non ferrous check
		} else {
			_totalQuantity = enquiry?.final_quote_buyer?.quote_per_product?.reduce((acc, item) => acc + item?.serviceable_weight, 0) / 1000
			_totalPoAmount = enquiry?.final_quote_buyer?.quote_per_product?.reduce(
				(acc, item) => acc + (item?.for_price + item?.ex_price) * item?.serviceable_weight,
				0
			)
			_hasLC = enquiry?.payment_terms_list?.find((item) => item.payment_term === 'LC')
			_isBitumen = enquiry?.commodity?.commodity_id === 'CON9VcB-5122' //bitumen's commodity id
			_isNonFerrous = ['COtx2Sx-1131', 'COd9sRH-8511', 'COLRzke-3499', 'COLRzke-3791'].includes(enquiry?.commodity?.commodity_id) //non ferrous check
		}
		return [_totalQuantity, _totalPoAmount, _hasLC, _isBitumen, _isNonFerrous]
	}, [enquiry, order])

	const hasPurchaseManager = !!enquiry?.cost_metrics?.buyer_procurement_manager || !!order?.cost_metrics?.buyer_procurement_manager
	const siteManagerUserId = order ? order?.buyer_list?.[0]?.site_manager?.user_id : enquiry?.site_manager?.user_id
	const hasSiteManager = !!siteManagerUserId
	const buyerOrganisationId = enquiry !== undefined ? enquiry?.buyer?.organisation_id : order?.buyer_list?.[0]?.buyer?.organisation_id

	const viewFileHandler = (type) => (file) => {
		if (!file) {
			return
		}
		dispatch(toggleFileViewer(true, { files: file }))
	}

	const uploadDocumentHandler = (type) => (file) => {
		if (!file) {
			return
		}

		dispatch(showLoader(true))
		uploadPunchOrderFile(file)
			.then((response) => {
				updateState(type)(response)
				dispatch(showToast(true, strings('uploaded_the_file_successfully'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				console.log(error)
				dispatch(showToast(true, strings('document_upload_failed'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const getMetricsFromFormState = () => {
		let _costMetrics = {
			cm_per_mt: formState?.cm_per_mt?.value,
			expected_delivery_date: new Date(formState?.expected_delivery_date?.value ? formState?.expected_delivery_date?.value : minDate).getTime(),
			freight_per_mt: formState?.freight_per_mt?.value,
			logistics_handler: formState?.logistics_handler?.value,
			make_to_order: formState?.make_to_order?.value,
			management_approval: formState?.management_approval?.value === '' ? null : formState?.management_approval?.value,
			marketing_expense: formState?.marketing_expense?.value,
			net_margin_per_mt: formState?.net_margin_per_mt?.value,
			discounting_partner: formState?.discounting_partner?.value === '' ? null : formState?.discounting_partner?.value,
			po: formState?.po?.value,
			credit_days: totalDays,
			po_date: new Date(formState?.po_date?.value).getTime(),
			original_po_number: formState?.original_po_number?.value,
			dispatch_start_date: formState?.dispatch_start_date?.value,
		}
		if (isBitumen) {
			_costMetrics = { ..._costMetrics, cha_cost: formState?.cha_cost?.value, duty: formState?.duty?.value, cfs: formState?.cfs?.value }
		}
		if (isNonFerrous) {
			_costMetrics = {
				..._costMetrics,
				supplier_price: formState?.supplier_price?.value,
				purchase_price: formState?.purchase_price?.value,
				customer_price: formState?.customer_price?.value,
				selling_price: formState?.selling_price?.value,
				quantity_discount_dn_cn: formState?.quantity_discount_dn_cn?.value,
				pricing_dn_cn: formState?.pricing_dn_cn?.value,
				warehousing_cost: formState?.warehousing_cost?.value,
				warehousing_provider: formState?.warehousing_provider?.value,
				hedging_profit_and_loss: formState?.hedging_profit_and_loss?.value,
				interest_deductions: formState?.interest_deductions?.value,

				hedging_brokerage_cost: formState?.hedging_brokerage_cost?.value,
			}
		}
		return _costMetrics
	}

	const patchDrawerData = (patch) => {
		if (order) {
			dispatch(updateDrawerMeta({ order: { ...order, ...patch } }))
		} else {
			dispatch(updateDrawerMeta({ enquiry: { ...enquiry, ...patch } }))
		}
	}

	const updateHandler = () => {
		dispatch(showLoader(true))
		let metrics = getMetricsFromFormState()
		updateOrderCostMetrics(order?.order_id, loggedInUser?.user_id, {
			...order?.cost_metrics,
			...metrics,
		})
			.then((response) => {
				dispatch(showToast(true, strings('msg_cost_metrics_submitted'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((error) => {
				dispatch(showToast(true, strings('msg_cost_metrics_submission_failed'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				update()
				dispatch(toggleDrawer(false))
				dispatch(showLoader(false))
			})
	}

	const costMetricsConfirm = () => {
		if (!formState?.expected_delivery_date?.dirty) {
			dispatch(
				toggleModal(true, MODAL_TYPES.CONFIRMATION, {
					overlayClickable: true,
					title: strings('msg_expected_delivery_date_confirm'),
					confirmAction: (actionType) => {
						actionType && updateCostMetrics()
						dispatch(toggleModal(false))
					},
				})
			)
		} else {
			updateCostMetrics()
		}
	}

	const updateCostMetrics = () => {
		dispatch(showLoader(true))
		let _costMetrics = getMetricsFromFormState()
		updateEnquiryCostMetrics(enquiryId, loggedInUser?.user_id, {
			buyer_procurement_manager: enquiry?.cost_metrics?.buyer_procurement_manager,
			..._costMetrics,
		})
			.then((response) => {
				update()
				dispatch(showToast(true, strings('msg_cost_metrics_submitted'), { type: TOAST_TYPE.SUCCESS }))
			})
			.catch((err) => {
				let error_msg = err?.response?.data?.message !== '' ?? strings('msg_cost_metrics_submission_failed')
				dispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
				dispatch(toggleDrawer(false))
			})
	}

	const isApproved = useMemo(() => {
		if (!totalDays || !totalPoAmount) {
			return false
		}

		let days = totalDays
		let n_margin = formState?.cm_per_mt?.value ?? 0 - formState?.marketing_expense?.value ?? 0
		let margin_threshold_perc_daywise = ((2 / 100) * days) / 30
		let amt = ((totalPoAmount / 1.18) * margin_threshold_perc_daywise) / totalQuantity
		return amt > n_margin
	}, [enquiry?.payment_tenure, formState?.cm_per_mt?.value, formState?.marketing_expense?.value, totalPoAmount, totalQuantity, totalDays])

	const isDisabled = useMemo(() => {
		if (enquiry?.enquiry_status === enquiryStatus.orderPlaced) {
			return true
		}
		return (
			formState?.cm_per_mt?.value === '' ||
			formState?.freight_per_mt?.value === '' ||
			!formState?.logistics_handler?.dirty ||
			!formState?.po?.value ||
			formState?.marketing_expense?.value === '' ||
			formState?.original_po_number?.value === '' ||
			(isApproved && !formState?.management_approval?.value) ||
			(hasLC && formState?.discounting_partner?.value === '')
		)
	}, [formState, isApproved])

	// sales admin is not allowed to change the po date of an order
	const isPoDateBlocked = isPoDateBlockedForRole(order?.order_id ?? orderId, loggedInUser?.role)

	const minDate = new Date(formState?.po_date?.value)
	minDate.setDate(minDate.getDate() + 1)

	return (
		<>
			<DrawerBodyWrapper>
				<FormGridWrapper>
					<Grid>
						<Input
							id={formState?.cm_per_mt?.id}
							type='number'
							label={formState?.cm_per_mt?.label}
							placeholder={formState?.cm_per_mt?.placeholder}
							value={formState?.cm_per_mt?.value}
							onChange={(e) => updateState('cm_per_mt')(e.target.value)}
							disabled={formState?.cm_per_mt?.disabled}
						/>
						<Input
							id={formState?.marketing_expense?.id}
							type='number'
							label={formState?.marketing_expense?.label}
							placeholder={formState?.marketing_expense?.placeholder}
							value={formState?.marketing_expense?.value}
							onChange={(e) => updateState('marketing_expense')(e.target.value)}
							disabled={formState?.marketing_expense?.disabled}
						/>
						<Input
							id={formState?.net_margin_per_mt?.id}
							type='number'
							label={formState?.net_margin_per_mt?.label}
							placeholder={formState?.net_margin_per_mt?.placeholder}
							value={formState?.net_margin_per_mt?.value}
							onChange={(e) => updateState('net_margin_per_mt')(e.target.value)}
							disabled={true}
						/>
						<Select
							id={formState?.logistics_handler?.id}
							label={formState?.logistics_handler?.label}
							placeholder={formState?.logistics_handler?.placeholder}
							data={logisticsHandlerTypes}
							value={logisticsHandlerTypes?.find((item) => formState?.logistics_handler?.value === item.key) ?? {}}
							displayKey={formState?.logistics_handler?.displayKey}
							primaryKey={formState?.logistics_handler?.primaryKey}
							onChange={(value) => updateState('logistics_handler')(value.key)}
							disabled={formState?.logistics_handler?.disabled}
						/>
						<Input
							id={formState?.freight_per_mt?.id}
							type='number'
							label={formState?.freight_per_mt?.label}
							placeholder={formState?.freight_per_mt?.placeholder}
							value={formState?.freight_per_mt?.value}
							onChange={(e) => updateState('freight_per_mt')(e.target.value)}
							disabled={formState?.freight_per_mt?.disabled}
						/>
						<Input
							id={formState?.original_po_number?.id}
							type='text'
							label={formState?.original_po_number?.label}
							placeholder={formState?.original_po_number?.placeholder}
							value={formState?.original_po_number?.value}
							onChange={(e) => updateState('original_po_number')(e.target.value)}
							disabled={orderId || viewOnly}
						/>
						<Calendar
							id={formState?.po_date?.id}
							label={formState?.po_date?.label}
							value={formState?.po_date?.value}
							onChange={updateState('po_date')}
							disabled={formState?.po_date?.disabled || isPoDateBlocked}
							minDate={enquiry?.enquiry_date ?? null}
						/>
						<Calendar
							id={formState?.expected_delivery_date?.id}
							label={formState?.expected_delivery_date?.label}
							value={formState?.expected_delivery_date?.value ? formState?.expected_delivery_date?.value : minDate}
							onChange={updateState('expected_delivery_date')}
							disabled={formState?.expected_delivery_date?.disabled}
							minDate={minDate}
						/>
						<FormElementContainer>
							<Label>{formState?.make_to_order?.label}</Label>
							<RadioGroupRow>
								<RadioOption>
									<RadioButton
										id='true-radio'
										size='1.125'
										type='bool'
										checked={formState?.make_to_order?.value === true}
										value={true}
										onChange={() => updateState('make_to_order')(true)}
										disabled={formState?.make_to_order?.disabled}
									/>
									<RadioOptionLabel>{strings('YES')}</RadioOptionLabel>
								</RadioOption>
								<RadioOption>
									<RadioButton
										id='false-radio'
										size='1.125'
										type='bool'
										checked={formState?.make_to_order?.value === false}
										value={false}
										onChange={() => updateState('make_to_order')(false)}
										disabled={formState?.make_to_order?.disabled}
									/>
									<RadioOptionLabel>{strings('NO')}</RadioOptionLabel>
								</RadioOption>
							</RadioGroupRow>
						</FormElementContainer>
						{isNonFerrous && (
							<>
								<Select
									id={formState?.supplier_price?.id}
									label={formState?.supplier_price?.label}
									data={pricingTypes}
									primaryKey='key'
									displayKey='label'
									placeholder={formState?.supplier_price?.placeholder}
									value={pricingTypes?.find((item) => item.key === formState?.supplier_price?.value) ?? {}}
									disabled={formState?.supplier_price?.disabled}
									required={formState?.supplier_price?.required}
									onChange={(val) => updateState('supplier_price')(val.key)}
								/>
								<Input
									type='number'
									id={formState?.purchase_price?.id}
									label={formState?.purchase_price?.label}
									placeholder={formState?.purchase_price?.placeholder}
									value={formState?.purchase_price?.value}
									disabled={formState?.purchase_price?.disabled}
									required={formState?.purchase_price?.required}
									onChange={(e) => updateState('purchase_price')(e.target.value)}
								/>
								<Select
									id={formState?.customer_price?.id}
									label={formState?.customer_price?.label}
									placeholder={formState?.customer_price?.placeholder}
									data={pricingTypes}
									primaryKey='key'
									displayKey='label'
									value={pricingTypes.find((item) => item.key === formState?.customer_price?.value) ?? {}}
									disabled={formState?.customer_price?.disabled}
									required={formState?.customer_price?.required}
									onChange={(val) => updateState('customer_price')(val.key)}
								/>
								<Input
									id={formState?.selling_price?.id}
									label={formState?.selling_price?.label}
									placeholder={formState?.selling_price?.placeholder}
									value={formState?.selling_price?.value}
									disabled={formState?.selling_price?.disabled}
									required={formState?.selling_price?.required}
									onChange={(e) => updateState('selling_price')(e.target.value)}
								/>
								<Select
									id={formState?.quantity_discount_dn_cn?.id}
									label={formState?.quantity_discount_dn_cn?.label}
									placeholder={formState?.quantity_discount_dn_cn?.placeholder}
									data={booltypes}
									primaryKey='key'
									displayKey='label'
									value={booltypes.find((item) => item.value === formState?.quantity_discount_dn_cn?.value) ?? {}}
									disabled={formState?.quantity_discount_dn_cn?.disabled}
									required={formState?.quantity_discount_dn_cn?.required}
									onChange={(val) => updateState('quantity_discount_dn_cn')(val.value)}
								/>
								<Select
									id={formState?.pricing_dn_cn?.id}
									label={formState?.pricing_dn_cn?.label}
									placeholder={formState?.pricing_dn_cn?.placeholder}
									data={booltypes}
									primaryKey='key'
									displayKey='label'
									value={booltypes?.find((item) => item.value === formState?.pricing_dn_cn?.value) ?? {}}
									disabled={formState?.pricing_dn_cn?.disabled}
									required={formState?.pricing_dn_cn?.required}
									onChange={(val) => updateState('pricing_dn_cn')(val.value)}
								/>
								<Input
									type='number'
									id={formState?.warehousing_cost?.id}
									label={formState?.warehousing_cost?.label}
									placeholder={formState?.warehousing_cost?.placeholder}
									value={formState?.warehousing_cost?.value}
									disabled={formState?.warehousing_cost?.disabled}
									required={formState?.warehousing_cost?.required}
									onChange={(e) => updateState('warehousing_cost')(e.target.value)}
								/>
								<Input
									id={formState?.warehousing_provider?.id}
									label={formState?.warehousing_provider?.label}
									placeholder={formState?.warehousing_provider?.placeholder}
									value={formState?.warehousing_provider?.value}
									disabled={formState?.warehousing_provider?.disabled}
									required={formState?.warehousing_provider?.required}
									onChange={(e) => updateState('warehousing_provider')(e.target.value)}
								/>
								<Input
									type='number'
									id={formState?.hedging_profit_and_loss?.id}
									label={formState?.hedging_profit_and_loss?.label}
									placeholder={formState?.hedging_profit_and_loss?.placeholder}
									value={formState?.hedging_profit_and_loss?.value}
									disabled={formState?.hedging_profit_and_loss?.disabled}
									required={formState?.hedging_profit_and_loss?.required}
									onChange={(e) => updateState('hedging_profit_and_loss')(e.target.value)}
								/>
								<Input
									id={formState?.interest_deductions?.id}
									label={formState?.interest_deductions?.label}
									placeholder={formState?.interest_deductions?.placeholder}
									value={formState?.interest_deductions?.value}
									disabled={formState?.interest_deductions?.disabled}
									required={formState?.interest_deductions?.required}
									onChange={(e) => updateState('interest_deductions')(e.target.value)}
								/>
								<Input
									type='number'
									id={formState?.hedging_brokerage_cost?.id}
									label={formState?.hedging_brokerage_cost?.label}
									placeholder={formState?.hedging_brokerage_cost?.placeholder}
									value={formState?.hedging_brokerage_cost?.value}
									disabled={formState?.hedging_brokerage_cost?.disabled}
									required={formState?.hedging_brokerage_cost?.required}
									onChange={(e) => updateState('hedging_brokerage_cost')(e.target.value)}
								/>
							</>
						)}
						{isBitumen && (
							<>
								<Input
									type='number'
									id={formState?.cha_cost?.id}
									label={formState?.cha_cost?.label}
									placeholder={formState?.cha_cost?.placeholder}
									value={formState?.cha_cost?.value}
									disabled={formState?.cha_cost?.disabled}
									required={formState?.cha_cost?.required}
									onChange={(e) => updateState('cha_cost')(e.target.value)}
								/>
								<Input
									id={formState?.duty?.id}
									label={formState?.duty?.label}
									placeholder={formState?.duty?.placeholder}
									value={formState?.duty?.value}
									disabled={formState?.duty?.disabled}
									required={formState?.duty?.required}
									onChange={(e) => updateState('duty')(e.target.value)}
								/>
								<Input
									type='number'
									id={formState?.cfs?.id}
									label={formState?.cfs?.label}
									placeholder={formState?.cfs?.placeholder}
									value={formState?.cfs?.value}
									disabled={formState?.cfs?.disabled}
									required={formState?.cfs?.required}
									onChange={(e) => updateState('cfs')(e.target.value)}
								/>
							</>
						)}
						{hasLC && (
							<Select
								id={formState?.discounting_partner?.id}
								label={formState?.discounting_partner?.label}
								placeholder={formState?.discounting_partner?.placeholder}
								data={discountingPartnerTypes}
								primaryKey={formState?.discounting_partner?.primaryKey}
								displayKey={formState?.discounting_partner?.displayKey}
								value={discountingPartnerTypes.find((item) => formState?.discounting_partner?.value === item.key) ?? {}}
								onChange={(value) => updateState('discounting_partner')(value.key)}
								disabled={formState?.discounting_partner?.disabled}
							/>
						)}
					</Grid>

					<SectionRowCard>
						<SectionRowTitle>
							{strings(['purchase', 'manager', 'details'])} <AsteriskIcon />
						</SectionRowTitle>
						<Button variant='outline' color='primary' small onClick={() => setIsPurchaseManagerModalOpen(true)}>
							{hasPurchaseManager ? strings('view') : strings('add')}
						</Button>
					</SectionRowCard>

					<SectionRowCard>
						<SectionRowTitle>{strings(['site', 'manager', 'details'])}</SectionRowTitle>
						<Button variant='outline' color='primary' small onClick={() => setIsSiteManagerModalOpen(true)}>
							{hasSiteManager ? strings('view') : strings('add')}
						</Button>
					</SectionRowCard>

					<MetricsData
						totalDays={totalDays}
						totalPoAmount={totalPoAmount}
						paymentTermsList={order?.buyer_list?.[0]?.payment_terms_list ?? enquiry?.payment_terms_list}
						pastDelays={pastDelays}
						supplierList={enquiry?.all_supplier_quotes ?? order?.supplier_list}
					/>

					<SectionRowCard>
						<SectionRowTitle isRequired={isApproved}>{strings('management', 'approval')}</SectionRowTitle>
						<FileActions>
							<IconActionButton
								disabled={!formState?.management_approval?.value}
								onClick={() => viewFileHandler('management_approval')(formState?.management_approval?.value)}
							>
								<DescriptionOutlined />
							</IconActionButton>
							<UploadIconButton
								icon={<PublishOutlined />}
								disabled={viewOnly}
								onChange={(file) => uploadDocumentHandler('management_approval')(file)}
							/>
						</FileActions>
					</SectionRowCard>

					<SectionRowCard>
						<SectionRowTitle isRequired={!formState?.po?.value}>{strings('customer', 'po')}</SectionRowTitle>
						<FileActions>
							<IconActionButton disabled={!formState?.po?.value} onClick={() => viewFileHandler('po')(formState?.po?.value)}>
								<DescriptionOutlined />
							</IconActionButton>
							<UploadIconButton icon={<PublishOutlined />} disabled={viewOnly} onChange={(file) => uploadDocumentHandler('po')(file)} />
						</FileActions>
					</SectionRowCard>
				</FormGridWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<FooterActions>
					<Button variant='outline' color='neutral' onClick={() => dispatch(toggleDrawer(false))}>
						{strings('cancel')}
					</Button>
					{orderId ? (
						<Button onClick={updateHandler} disabled={isDisabled || viewOnly}>
							{strings('update')}
						</Button>
					) : (
						<Button onClick={costMetricsConfirm} disabled={isDisabled || viewOnly}>
							{edit ? strings('update') : strings('submit')}
						</Button>
					)}
				</FooterActions>
			</DrawerFooterWrapper>
			<PurchaseManagerDetailsModal
				isOpen={isPurchaseManagerModalOpen}
				onClose={() => setIsPurchaseManagerModalOpen(false)}
				enquiry={enquiry}
				order={order}
				update={update}
				onSaved={patchDrawerData}
				editable={hasPurchaseManager}
				organisationId={buyerOrganisationId}
				viewOnly={viewOnly}
			/>
			<SiteManagerDetailsModal
				isOpen={isSiteManagerModalOpen}
				onClose={() => setIsSiteManagerModalOpen(false)}
				enquiry={enquiry}
				order={order}
				update={update}
				onSaved={patchDrawerData}
				editable={hasSiteManager}
				userId={siteManagerUserId}
				organisationId={buyerOrganisationId}
				viewOnly={viewOnly}
			/>
		</>
	)
}

export default AddCostMetricsV2
